<template>
  <div class="signup">
    <div v-if="step <= 3" class="signup-stepper">
      <h2>商戶申請</h2>
      <span class="signup__remark">*必須填寫</span>
      <ul>
        <li v-for="item in steps"
            :key="item.value"
            :class="{ active: step === item.value }"
        >
          <span class="signup-stepper__step">{{ item.value }}</span>
          <span class="signup-stepper__label">{{ item.text }}</span>
        </li>
      </ul>
    </div>
    <transition :name="transitionName" mode="out-in">
      <CompanyInfo v-if="step === 1"
                   v-model="item"
                   class="step-component"
                   ref="step1"
                   @updated="$emit('updated')"
                   :merchant-tag-options="merchantTagOptions"
                   :merchant-sub-tag-options="merchantSubTagOptions"
      ></CompanyInfo>
      <ContactInfo v-if="step === 2"
                   v-model="item"
                   class="step-component"
                   ref="step2"
                   @updated="$emit('updated')"
      ></ContactInfo>
      <Preview v-if="step === 3"
               :item="item"
               class="step-component"
               ref="step3"
               @updated="$emit('updated')"
               :merchant-tag-with-sub-tags="merchantTagWithSubTags"
      ></Preview>
      <Success v-if="step === 4"
               :ref-id="refId"
               class="step-component"
               @updated="$emit('updated')"
      ></Success>
    </transition>
    <hr v-if="step < 3">
    <div v-if="step <= 3" class="signup__actions d-flex justify-content-between">
      <b-button v-show="step > 1"
                :disabled="loading"
                variant="outline-secondary"
                size="lg"
                v-stream:click="onPrev$"
      >
        上一步
      </b-button>
      <div class="spacer"></div>
      <b-button v-show="step === 3"
                :disabled="loading"
                variant="primary"
                size="lg"
                v-stream:click="onSubmit$"
      >
        <template>提交</template>
      </b-button>
      <b-button v-show="step < 3"
                :disabled="loading"
                variant="primary"
                size="lg"
                v-stream:click="onNext$"
      >
        下一步
      </b-button>
    </div>
    <LoadingSpinner v-show="loading"></LoadingSpinner>
  </div>
</template>

<script>
import { from } from 'rxjs';
import {
  map,
  tap,
  filter,
  switchMap,
} from 'rxjs/operators';
import { BButton } from 'bootstrap-vue';
import http from '@/helpers/http';
import { getErrorMsg } from '@/helpers/error-msg';
import toastSettings from '@/helpers/toast-settings';
import CompanyInfo from '@/components/signup/CompanyInfo.vue';
import ContactInfo from '@/components/signup/ContactInfo.vue';
import Preview from '@/components/signup/Preview.vue';
import Success from '@/components/signup/Success.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import tagAndSubTagMixin from '@/mixins/merchant/tag-and-sub-tag';

const ERRORS = {
  DUPLICATED: '此用戶已經註冊過，請確認資料正確再重新提交',
};

export default {
  components: {
    CompanyInfo,
    ContactInfo,
    Preview,
    Success,
    BButton,
    LoadingSpinner,
  },
  mixins: [tagAndSubTagMixin],
  data: () => ({
    step: 1,
    steps: [
      {
        text: '公司資料',
        value: 1,
      },
      {
        text: '聯絡人資料',
        value: 2,
      },
      {
        text: '預覽',
        value: 3,
      },
    ],
    item: {
      shopType: 'physical',
      accTypeInRegister: 'NORMAL',
      tags: [{
        id: null,
        children: [],
      }],
      images: [],
    },
    refId: null,
    transitionName: 'slide-left',
    loading: false,
  }),
  watch: {
    step(newVal, oldVal) {
      this.transitionName = oldVal < newVal ? 'slide-left' : 'slide-right';
    },
  },
  domStreams: ['onNext$', 'onPrev$', 'onSubmit$'],
  subscriptions() {
    const validate$ = () => from(this.$refs[`step${this.step}`].validate({
      isScrollToTopIfFail: this.step !== 3,
    }))
      .pipe(
        filter((success) => !!success),
      );

    const signup$ = (data) => http({
      method: 'POST',
      url: '/agc-merchant-api/auth/signup',
      data,
    }, {
      next: (refId) => {
        this.step = 4;
        this.refId = refId;
      },
      error: ({ data: { msg } }) => {
        this.$bvToast.toast(getErrorMsg(msg, ERRORS), {
          ...toastSettings,
          title: '申請失敗',
          toaster: 'b-toaster-top-center',
        });
      },
      complete: () => {
        this.loading = false;
      },
    });

    return {
      submit: this.onSubmit$
        .pipe(
          switchMap(validate$),
          tap(() => {
            this.loading = true;
          }),
          map(() => ({
            ...this.item,
          })),
          switchMap(signup$),
        ),
      prev: this.onPrev$
        .pipe(
          tap(() => {
            if (this.step > 1) {
              this.step -= 1;
            }
          }),
        ),
      next: this.onNext$
        .pipe(
          switchMap(validate$),
          tap(() => {
            if (this.step < 3) {
              this.step += 1;
            }
          }),
        ),
    };
  },
  mounted() {
    this.$emit('updated');
  },
};
</script>

<style lang="scss">
@import "../../assets/scss/transition/slide";
@import "../../assets/scss/transition/fade";

</style>

<style scoped lang="scss">
.signup {
  position: relative;

  &__remark {
    position: absolute;
    top: 2px;
    right: -4px;
    color: #f54b4e;
    font-size: 14px;
    font-weight: 500;
  }

  &__actions {
    .btn {
      padding: 10px 18px ;
      width: 120px;
      font-size: 15px;

      &-outline-secondary{
        background-color: #F9F9FA;
        border: 1px solid #DDE1E5 !important;
        color: #636D77;
      }
    }
  }
}

.signup-stepper {
  text-align: center;

  & h2 {
    color: #174169;
    font-size: 18px;
    line-height: 25px;
    margin-bottom: 24px;
  }

  & ul {
    position: relative;
    display: inline-flex;
    list-style: none;
    padding: 0;
    margin-bottom: 24px;

    &:before {
      content: " ";
      position: absolute;
      top: 20px;
      left: 40px;
      right: 40px;
      display: block;
      height: 2px;
      background: #bdc5cd;
      z-index: 0;
    }
  }

  & li {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 80px;
    z-index: 1;

    @include respond(phone) {
      width: 60px;
    }

    &:not(:last-child) {
      margin-right: 66px;
    }

    &.active {
      & .signup-stepper {
        &__step {
          border-color: #174169;
          background-color: #174169;
          color: white;
        }

        &__label {
          color: #174169;
        }
      }
    }
  }

  &__step {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid #bdc5cd;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    background: white;
    color: #bdc5cd;
    font-size: 18px;
    font-weight: 400;
    margin-bottom: 6px;
  }

  &__label {
    color: #aab4bd;
    font-weight: 400;

    @include respond(phone) {
      font-size: 12px;
    }
  }
}

.step-component {
  transition: all 0.2s cubic-bezier(0.55, 0, 0.1, 1);
}

hr {
  margin-bottom: 24px;
}

::v-deep {
  .form-control {
    height: 100%;
    padding: 10px 14px;
    font-size: 15px;

    &::-moz-placeholder {
      color: #96a1ad;
      opacity: 1;
      font-weight: 300;
    }
    &::-ms-input-placeholder {
      color: #96a1ad;
      opacity: 1;
      font-weight: 300;
    }
    &::placeholder {
      color: #96a1ad;
      opacity: 1;
      font-weight: 300;
    }
  }

  .custom-control{
    color: #3a4047;
    font-size: 15px;
}
}
</style>
